import type { RouterOutputs } from "@/lib/trpc";
import { Flame, Activity, CheckCircle2, TrendingUp } from "lucide-react";
import { useI18n } from "@/lib/i18n";

/**
 * Weekly Progress — self-only metrics, rendered from server payloads. Per D3:
 * **daily streak is removed**; weekly streak is the only streak shown;
 * follow-through is self-only and never feeds Team Pulse. No client computation.
 */
type Momentum = RouterOutputs["momentum"]["summary"];

function Tile({
  icon,
  label,
  value,
  unit,
  tint,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  unit?: string;
  /** Soft background tint per metric (presentation only). */
  tint: string;
}) {
  return (
    <div className={`rounded-2xl border border-border/40 px-3.5 py-3 ${tint}`}>
      <div className="mb-1.5">{icon}</div>
      <div className="text-[10.5px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="flex items-baseline gap-1">
        <span className="text-2xl font-bold tabular-nums">{value}</span>
        {unit ? <span className="text-[11px] text-muted-foreground">{unit}</span> : null}
      </div>
    </div>
  );
}

export function WeeklyProgressCard({
  momentum,
  followThroughPct,
}: {
  momentum: Momentum;
  /** Self follow-through %, server-provided (insights.trajectory). */
  followThroughPct: number | null;
}) {
  const { locale } = useI18n();
  const isAr = locale === "ar";
  return (
    <section className="rounded-3xl border border-border/60 bg-card p-4 shadow-sm" data-testid="weekly-progress">
      <div className="flex items-center justify-between mb-3 px-1">
        <div className="text-[11px] font-semibold tracking-wider text-muted-foreground">
          {isAr ? "التقدم الأسبوعي" : "WEEKLY PROGRESS"}
        </div>
        <span className="text-[11px] font-medium text-coral">{isAr ? "دليل الظهور" : "Visibility guide"}</span>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Tile
          icon={<Flame className="h-4 w-4 text-coral" />}
          label={isAr ? "سلسلة أسبوعية" : "Weekly streak"}
          value={String(momentum.weeklyCurrent)}
          unit={isAr ? "أسابيع" : "weeks"}
          tint="bg-coral-soft/30"
        />
        <Tile
          icon={<TrendingUp className="h-4 w-4 text-sky-600" />}
          label={isAr ? "نسبة الإنجاز" : "Follow-through"}
          value={followThroughPct == null ? "—" : `${Math.round(followThroughPct)}%`}
          unit={isAr ? "مقارنة بآخر 8 أسابيع" : "vs last 8 wks"}
          tint="bg-sky-500/10"
        />
        <Tile
          icon={<CheckCircle2 className="h-4 w-4 text-emerald-600" />}
          label={isAr ? "معوقات تمت إزالتها" : "Blockers cleared"}
          value={String(momentum.blockersResolvedWeek)}
          unit={isAr ? "هذا الأسبوع" : "this week"}
          tint="bg-emerald-500/10"
        />
        <Tile
          icon={<Activity className="h-4 w-4 text-violet-500" />}
          label={isAr ? "إشارات العمل" : "Work signals"}
          value={String(momentum.signalsThisWeek)}
          unit={isAr ? "هذا الأسبوع" : "this week"}
          tint="bg-violet-500/10"
        />
      </div>
      <p className="mt-3 px-1 text-[11px] text-muted-foreground">
        {isAr
          ? "عرض إجمالي. تبقى المقاييس الفردية خاصة."
          : "Aggregate view. Individual metrics stay private."}
      </p>
    </section>
  );
}
